import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useLivePoll } from "../lib/live";
import { StatusChip, docProtocol, boardColumnForDocument } from "../lib/format";
import { Button } from "../components/ui/button";
import { MapContainer, TileLayer, Popup, CircleMarker, Polyline, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Sparkles, Route as RouteIcon, Users, Layers, Clock, Ruler, Zap,
  CheckCircle2, MapPin,
} from "lucide-react";
import { toast } from "sonner";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function parseCoords(item) {
  if (!item) return null;
  const lat = Number(item.lat);
  const lng = Number(item.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
  return [lat, lng];
}

function FitToPoints({ points, maxZoom = 15 }) {
  const map = useMap();
  const key = (points || []).map((p) => `${p?.[0]},${p?.[1]}`).join("|");
  useEffect(() => {
    const valid = (points || []).filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (!valid.length) return;
      if (valid.length === 1) map.setView(valid[0], Math.min(15, maxZoom));
      else map.fitBounds(L.latLngBounds(valid), { padding: [48, 48], maxZoom });
    }, 80);
    return () => clearTimeout(timer);
  }, [map, maxZoom, key]);
  return null;
}

// Palette of route colors (matches design brand colors)
const ROUTE_COLORS = ["#2C48E0", "#43E695", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];
const STATUS_COLOR = {
  delivered: "#10B981", returned: "#EF4444", out_for_delivery: "#FF5A00",
  attempted: "#EAB308", received: "#94A3B8",
};

export default function RoutesPage() {
  const { t } = useI18n();
  const [docs, setDocs] = useState([]);
  const [deliverers, setDeliverers] = useState([]);
  const [selectedDeliverers, setSelectedDeliverers] = useState([]);
  const [groupBy, setGroupBy] = useState("kmeans");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    const [{ data: d }, { data: u }] = await Promise.all([
      api.get("/documents"),
      api.get("/users?role=deliverer"),
    ]);
    setDocs(d || []);
    setDeliverers(u);
    setSelectedDeliverers((prev) =>
      prev.length === 0 && u.length
        ? u.slice(0, Math.min(3, u.length)).map((x) => x.id)
        : prev
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useLivePoll(load, 20000);

  const unassigned = useMemo(
    () => docs.filter((d) => ["received", "sorting", "out_for_delivery"].includes(d.status)),
    [docs]
  );

  const mapPoints = useMemo(
    () => docs.map((d) => parseCoords(d)).filter(Boolean),
    [docs],
  );

  const toggleDeliverer = (id) => setSelectedDeliverers((s) =>
    s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
  );

  const optimize = async () => {
    if (!selectedDeliverers.length) { toast.error("Selecione ao menos 1 entregador"); return; }
    if (!unassigned.length) { toast.info("Sem documentos aguardando rota"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/routes/optimize", {
        deliverer_ids: selectedDeliverers,
        document_ids: unassigned.map((d) => d.id),
        group_by: groupBy,
      });
      setPlan(data);
      setDialogOpen(true);
    } catch (e) { toast.error("Falha ao otimizar"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      const assignments = plan.routes
        .filter((r) => r.stops.length)
        .map((r) => ({
          deliverer_id: r.deliverer_id,
          document_ids: r.stops.map((s) => s.id),
        }));
      const { data } = await api.post("/routes/apply", { assignments });
      toast.success(`${data.applied} documentos atribuídos em ${data.routes.length} rota(s)`);
      setDialogOpen(false);
      setPlan(null);
      load();
    } catch (e) { toast.error("Falha ao aplicar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="card-soft px-5 py-4 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-[var(--text-2)] font-medium">Logística</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">{t("nav_routes")}</h1>
          <p className="text-xs text-[var(--text-2)] mt-1">
            {docs.length} entrega(s) no mapa · <span className="font-semibold text-[var(--warning-text)]">{unassigned.length} aguardando rota</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {Object.entries(STATUS_COLOR).map(([k, c]) => (
            <div key={k} className="flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--surface-2)]">
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: c }} />
              <span className="text-[10px] font-medium">{t(`status_${k}`)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Optimization controls */}
      <div className="card-soft p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[var(--brand)] to-[var(--brand-2)] flex items-center justify-center text-white shrink-0">
            <Sparkles size={20} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold">Otimização automática de rotas</div>
            <div className="text-xs text-[var(--text-2)] mt-0.5">
              Agrupa entregas por região (K-means) ou cidade e otimiza a ordem dos stops via nearest-neighbor.
            </div>
          </div>
          <Button
            onClick={optimize}
            disabled={busy || !unassigned.length}
            className="rounded-2xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11 text-xs font-semibold px-5"
            data-testid="optimize-btn"
          >
            {busy ? "Calculando..." : <><Zap size={14} className="mr-1.5" /> Otimizar</>}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users size={14} className="text-[var(--text-2)]" />
              <div className="text-xs font-semibold text-[var(--text-2)]">
                Entregadores ({selectedDeliverers.length}/{deliverers.length})
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {deliverers.length === 0 && (
                <div className="text-xs text-[var(--text-3)]">Nenhum entregador cadastrado</div>
              )}
              {deliverers.map((d) => {
                const active = selectedDeliverers.includes(d.id);
                return (
                  <button
                    key={d.id}
                    onClick={() => toggleDeliverer(d.id)}
                    className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition-colors ${
                      active
                        ? "bg-[var(--brand)] text-white"
                        : "bg-[var(--surface-2)] text-[var(--text-2)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]"
                    }`}
                    data-testid={`deliv-chip-${d.id}`}
                  >
                    {active && <CheckCircle2 size={12} />} {d.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Layers size={14} className="text-[var(--text-2)]" />
              <div className="text-xs font-semibold text-[var(--text-2)]">Método de agrupamento</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setGroupBy("kmeans")}
                className={`text-left p-3 rounded-2xl border transition-all ${
                  groupBy === "kmeans"
                    ? "bg-[var(--brand-soft)] border-[var(--brand)]"
                    : "bg-[var(--surface-2)] border-transparent hover:bg-[var(--surface-hover)]"
                }`}
                data-testid="method-kmeans"
              >
                <div className={`text-xs font-bold ${groupBy === "kmeans" ? "text-[var(--brand)]" : "text-[var(--text-1)]"}`}>
                  K-means (geográfico)
                </div>
                <div className="text-[10px] text-[var(--text-2)] mt-0.5 leading-snug">
                  Clusters por proximidade de GPS. Ideal para densidade urbana.
                </div>
              </button>
              <button
                onClick={() => setGroupBy("city")}
                className={`text-left p-3 rounded-2xl border transition-all ${
                  groupBy === "city"
                    ? "bg-[var(--brand-soft)] border-[var(--brand)]"
                    : "bg-[var(--surface-2)] border-transparent hover:bg-[var(--surface-hover)]"
                }`}
                data-testid="method-city"
              >
                <div className={`text-xs font-bold ${groupBy === "city" ? "text-[var(--brand)]" : "text-[var(--text-1)]"}`}>
                  Por cidade
                </div>
                <div className="text-[10px] text-[var(--text-2)] mt-0.5 leading-snug">
                  Um entregador por cidade. Útil para rotas interestaduais.
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="card-soft leaflet-map-shell h-[min(65vh,720px)] min-h-[280px]" data-testid="routes-map">
        <MapContainer center={mapPoints[0] || [-15.7, -47.9]} zoom={mapPoints.length ? 12 : 4} style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          <FitToPoints points={mapPoints} />

          {/* Base markers */}
          {docs.map((d) => {
            const coords = parseCoords(d);
            if (!coords) return null;
            const column = boardColumnForDocument(d);
            return (
            <CircleMarker
              key={`${d.id}:${coords.join(",")}`}
              center={coords}
              radius={8}
              pathOptions={{
                color: STATUS_COLOR[column] || "#999",
                fillColor: STATUS_COLOR[column] || "#999",
                fillOpacity: 0.85, weight: 2,
              }}
            >
              <Popup>
                <div className="text-xs space-y-1">
                  <div className="font-mono font-semibold">{d.number}</div>
                  <div>{d.recipient_name}</div>
                  <div className="text-muted-foreground">{d.address}, {d.city}</div>
                  <StatusChip status={column} t={t} />
                </div>
              </Popup>
            </CircleMarker>
            );
          })}

          {/* Optimized route polylines */}
          {plan && plan.routes.map((r, idx) => {
            if (r.stops.length < 2) return null;
            const positions = r.stops.map((s) => parseCoords(s)).filter(Boolean);
            const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
            return (
              <React.Fragment key={r.deliverer_id}>
                <Polyline positions={positions} pathOptions={{ color, weight: 4, opacity: 0.7 }}>
                  <Tooltip sticky>
                    <div className="text-xs font-semibold">{r.deliverer_name}</div>
                    <div className="text-[10px]">{r.stops.length} stops · {r.km} km · {r.minutes} min</div>
                  </Tooltip>
                </Polyline>
                {r.stops.map((s, i) => {
                  const coords = parseCoords(s);
                  if (!coords) return null;
                  return (
                    <CircleMarker
                      key={s.id + "_num"}
                      center={coords}
                      radius={10}
                      pathOptions={{ color, fillColor: color, fillOpacity: 1, weight: 2 }}
                    >
                      <Tooltip permanent direction="center">
                        <span className="text-[10px] font-bold text-white">{i + 1}</span>
                      </Tooltip>
                    </CircleMarker>
                  );
                })}
              </React.Fragment>
            );
          })}
        </MapContainer>
      </div>

      {/* Plan review dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-3xl max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold flex items-center gap-2">
              <Sparkles size={18} className="text-[var(--brand)]" /> Plano de rotas otimizado
            </DialogTitle>
            <DialogDescription className="text-xs">
              Revise as rotas propostas antes de aplicar. Cada entregador receberá uma notificação.
            </DialogDescription>
          </DialogHeader>

          {plan && (
            <div className="space-y-4">
              {/* Aggregate stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-2xl bg-[var(--surface-2)] p-3">
                  <div className="text-[10px] text-[var(--text-2)] font-semibold uppercase tracking-wide">Rotas</div>
                  <div className="font-display text-2xl font-bold mt-0.5">{plan.routes.filter((r) => r.stops.length).length}</div>
                </div>
                <div className="rounded-2xl bg-[var(--surface-2)] p-3">
                  <div className="text-[10px] text-[var(--text-2)] font-semibold uppercase tracking-wide">Entregas</div>
                  <div className="font-display text-2xl font-bold mt-0.5">{plan.total_stops}</div>
                </div>
                <div className="rounded-2xl bg-[var(--surface-2)] p-3">
                  <div className="text-[10px] text-[var(--text-2)] font-semibold uppercase tracking-wide">Total km</div>
                  <div className="font-display text-2xl font-bold mt-0.5">{plan.total_km}</div>
                </div>
              </div>

              {/* Route cards */}
              <div className="space-y-3">
                {plan.routes.map((r, idx) => {
                  const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
                  return (
                    <div key={r.deliverer_id} className="rounded-2xl border-2 bg-[var(--surface)] p-4" style={{ borderColor: color + "40" }} data-testid={`plan-route-${idx}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold" style={{ background: color }}>
                            {idx + 1}
                          </div>
                          <div className="min-w-0">
                            <div className="font-display font-bold text-sm truncate">{r.deliverer_name || "—"}</div>
                            <div className="text-xs text-[var(--text-2)] flex items-center gap-3 mt-0.5">
                              <span className="inline-flex items-center gap-1"><RouteIcon size={11} />{r.stops.length} stops</span>
                              <span className="inline-flex items-center gap-1"><Ruler size={11} />{r.km} km</span>
                              <span className="inline-flex items-center gap-1"><Clock size={11} />~{Math.round(r.minutes / 60 * 10) / 10}h</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {r.stops.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[var(--border-c)]">
                          <div className="flex flex-wrap gap-1.5">
                            {r.stops.slice(0, 12).map((s, i) => (
                              <div key={s.id} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--surface-2)] px-2 py-1 text-xs" data-testid={`stop-${idx}-${i}`}>
                                <span className="w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center text-white" style={{ background: color }}>{i + 1}</span>
                                <span className="font-mono text-[10px] text-[var(--text-2)]">{s.protocol_number}</span>
                                <span className="truncate max-w-[120px]">{s.recipient_name}</span>
                                <MapPin size={10} className="text-[var(--text-3)]" />
                                <span className="text-[10px] text-[var(--text-2)]">{s.city}</span>
                              </div>
                            ))}
                            {r.stops.length > 12 && (
                              <span className="text-[10px] text-[var(--text-2)] self-center">+{r.stops.length - 12} mais...</span>
                            )}
                          </div>
                        </div>
                      )}

                      {r.stops.length === 0 && (
                        <div className="mt-3 pt-3 border-t border-[var(--border-c)] text-xs text-[var(--text-3)]">
                          Nenhuma entrega alocada para este entregador
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 h-11 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--surface-hover)] text-xs font-semibold"
                  data-testid="plan-cancel"
                >
                  Cancelar
                </button>
                <Button
                  onClick={apply}
                  disabled={busy}
                  className="flex-1 rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11 text-xs font-semibold"
                  data-testid="plan-apply"
                >
                  {busy ? "Aplicando..." : <><CheckCircle2 size={14} className="mr-1.5" /> Aplicar {plan.total_stops} atribuições</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
